import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";

// Website pages a read-only page links to: label of the page and of the record kind, with its icon.
const PAGES = [
  { path: "/local-vendor", page: "Local Vendor", kind: "Local vendor", icon: "fas fa-shuttle-van" },
  { path: "/hotels", page: "Hotels", kind: "Hotel", icon: "fas fa-hotel" },
  { path: "/airport", page: "Airport", kind: "Airport", icon: "fas fa-plane" },
  { path: "/nonfly-activity-location", page: "Activity Location", kind: "Activity location", icon: "fas fa-map-marker-alt" },
];
const pageOf = (to) => PAGES.find((p) => to.split("?")[0] === p.path) ?? { page: "that page", kind: "Record", icon: "fas fa-external-link-alt" };

const LeaveContext = createContext(null);

/** The panel: slides down from the top; Go navigates (same tab), Cancel / Esc / backdrop click stays. */
function LeavePanel({ target, from, onGo, onCancel }) {
  const goRef = useRef(null);
  const cancelRef = useRef(null);
  const info = pageOf(target.to);
  useEffect(() => {
    if (goRef.current) goRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
      // Keep Tab inside the two buttons.
      if (e.key === "Tab") {
        e.preventDefault();
        const next = document.activeElement === goRef.current ? cancelRef.current : goRef.current;
        if (next) next.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return ReactDOM.createPortal(
    <div className="mst-leave" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="mst-leave__panel" role="dialog" aria-modal="true" aria-label={`Open ${target.name} in ${info.page}`}>
        <span className="mst-leave__icon">
          <i className={info.icon} aria-hidden="true" />
        </span>
        <div className="mst-leave__text">
          <strong>{target.name}</strong>
          <span>
            {info.kind} · opens the {info.page} page
          </span>
          <small>You will leave {from}.</small>
        </div>
        <div className="mst-leave__actions">
          <button ref={cancelRef} type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
            Cancel
          </button>
          <button ref={goRef} type="button" className="btn btn-primary btn-sm" onClick={onGo}>
            Go to {info.page} <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Wrap a read-only page: its ConfirmLinks ask before leaving it. from = the page name shown in the panel. */
export function LeaveConfirmProvider({ from, children }) {
  const history = useHistory();
  const [target, setTarget] = useState(null);
  const cancel = () => setTarget(null);
  return (
    <LeaveContext.Provider value={setTarget}>
      {children}
      {target && (
        <LeavePanel
          target={target}
          from={from}
          onCancel={cancel}
          onGo={() => {
            setTarget(null);
            history.push(target.to);
          }}
        />
      )}
    </LeaveContext.Provider>
  );
}

/**
 * A link to another website page that first asks (top panel: Go to <page> / Cancel). Every click is caught, also Ctrl /
 * middle click: a new tab loses the login (sessionStorage). Outside a LeaveConfirmProvider it navigates directly.
 * name = the record shown in the panel (default: X of a title "Open X in Page" / "Open X").
 */
export function ConfirmLink({ to, name, title, className, children }) {
  const ask = useContext(LeaveContext);
  const history = useHistory();
  const fromTitle = (title || "").replace(/^Open /, "").replace(/ in [^ ]+( [^ ]+)?$/, "");
  const open = (e) => {
    e.preventDefault();
    if (ask) ask({ to, name: name || fromTitle || to });
    else history.push(to);
  };
  return (
    <a href={to} className={className} title={title} onClick={open} onAuxClick={open}>
      {children}
    </a>
  );
}
